import { ArrowsLeftRightIcon } from "@phosphor-icons/react/dist/csr/ArrowsLeftRight";
import { ChartPieSliceIcon } from "@phosphor-icons/react/dist/csr/ChartPieSlice";
import { ChatCircleIcon } from "@phosphor-icons/react/dist/csr/ChatCircle";
import { FlagIcon } from "@phosphor-icons/react/dist/csr/Flag";
import { QuestionIcon } from "@phosphor-icons/react/dist/csr/Question";
import { ScalesIcon } from "@phosphor-icons/react/dist/csr/Scales";
import { UploadSimpleIcon } from "@phosphor-icons/react/dist/csr/UploadSimple";
import { UsersIcon } from "@phosphor-icons/react/dist/csr/Users";
import { Link } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  Toolbar,
  ToolbarButton,
  ToolbarGroup,
} from "@volca/ui/components/ui/toolbar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import { SectionHeading } from "@/profitability/records/section-heading";
import { Rating } from "@/profitability/review";
import type { OverviewInsight } from "./types";

/**
 * What kind of line it is, at a glance. An estimate line (actuals against
 * what was estimated) weighs one against the other, so it gets scales.
 */
const Mark = ({ insight }: { insight: OverviewInsight }) => {
  // The mark says what kind of line it is; only the flags line takes a hue.
  const className = cn("mt-0.5 size-4 flex-none text-subtle", {
    "text-warning": insight.kind === "flags",
  });
  switch (insight.kind) {
    case "flags":
      return <FlagIcon aria-hidden className={className} />;
    case "segment":
      return <UsersIcon aria-hidden className={className} />;
    case "mix":
      return <ChartPieSliceIcon aria-hidden className={className} />;
    case "changes":
      return <ArrowsLeftRightIcon aria-hidden className={className} />;
    case "missing":
      return <QuestionIcon aria-hidden className={className} />;
    case "estimate":
      return <ScalesIcon aria-hidden className={className} />;
    default: {
      const exhaustive: never = insight.kind;
      return exhaustive;
    }
  }
};

/** How many lines the dashboard shows. The rest open with View all. */
export const INSIGHT_PREVIEW = 3;

/**
 * What stands out, one line each, worked out from the report's figures: a
 * mark for what kind of line it is, then the line across the full width.
 * Its rating and the way to ask Volca why sit in a toolbar at the end of
 * the line, shown on hover. A line about figures not in yet carries the
 * way to add them (`action`): it is the next thing to do.
 */
export const OverviewInsights = ({
  businessId,
  insights,
  onAsk,
  action,
  more,
  limit,
}: {
  businessId: string;
  insights: OverviewInsight[];
  onAsk: (insight: OverviewInsight) => void;
  /** The link a line offers, when it has one (`missingUpload`). */
  action?: (insight: OverviewInsight) => { label: string; to: string } | null;
  /** Opens the rest of the reading, from a page that only previews it. */
  more?: { label: string; to: string } | null;
  /** Show this many lines. Unset shows every line, and the list scrolls. */
  limit?: number;
}) => {
  const lines = insights.filter((insight) => insight.text.trim());
  const shown = limit === undefined ? lines : lines.slice(0, limit);
  if (shown.length === 0) return null;
  return (
    <section
      aria-label="What stands out"
      className="flex min-w-0 flex-col gap-2"
    >
      <Stack direction="row" align="center" justify="between" gap={2}>
        <SectionHeading>What stands out</SectionHeading>
        {more && (
          <Button
            variant="ghost"
            size="sm"
            nativeButton={false}
            render={<Link to={more.to} />}
          >
            {more.label}
          </Button>
        )}
      </Stack>
      <ul
        className={cn("flex flex-col gap-3", {
          "max-h-96 overflow-y-auto overscroll-contain": limit === undefined,
        })}
      >
        {shown.map((insight) => (
          <li
            key={insight.id}
            className="group relative flex items-start gap-x-2"
          >
            <Mark insight={insight} />
            <span className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
              <Text
                variant="body"
                className="max-w-prose text-pretty [overflow-wrap:anywhere] text-ink"
              >
                {insight.text}
              </Text>
              <InsightAction link={action?.(insight) ?? null} />
              <Toolbar
                aria-label="Line actions"
                className="pointer-events-none absolute end-0 top-0 z-10 gap-0.5 p-0.5 opacity-0 transition-opacity group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100 pointer-coarse:pointer-events-auto pointer-coarse:static pointer-coarse:self-end pointer-coarse:opacity-100 pointer-coarse:[&_button]:min-h-11 pointer-coarse:[&_button]:min-w-11"
              >
                <ToolbarGroup className="gap-0.5">
                  {insight.insight_id && limit === undefined && (
                    <Rating
                      businessId={businessId}
                      subjectType="insight"
                      subjectId={insight.insight_id}
                      feedback={insight.feedback}
                      label={insight.text}
                    />
                  )}
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <ToolbarButton
                          render={
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`Ask why: ${insight.text}`}
                              onClick={() => onAsk(insight)}
                            />
                          }
                        />
                      }
                    >
                      <ChatCircleIcon aria-hidden className="size-3.5" />
                    </TooltipTrigger>
                    <TooltipContent>Ask Volca why</TooltipContent>
                  </Tooltip>
                </ToolbarGroup>
              </Toolbar>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};

const InsightAction = ({
  link,
}: {
  link: { label: string; to: string } | null;
}) =>
  link ? (
    <Button
      size="xs"
      variant="outline"
      className="pointer-coarse:min-h-11"
      nativeButton={false}
      render={<Link to={link.to} />}
    >
      <UploadSimpleIcon aria-hidden data-icon="inline-start" />
      {link.label}
    </Button>
  ) : null;
